import { useQuery } from '@tanstack/react-query';
import { useAuthenticatedFetch } from './useAuth';

export type DiagramType = 'genogram' | 'flowchart' | 'mindmap' | 'network' | 'org' | 'database' | 'uml' | 'generic';

export interface DiagramViewDefinition {
  type: string;
  parentDiagramId?: string;
  viewQueryId?: string;
  viewParams?: Record<string, string>;
  sourceGraphVersion?: number;
  resourceCount?: number;
  groupLabel?: string;
  /** Canonical folder-model projection rendered by this diagram. */
  projection?: 'people_chart' | 'planning_chart' | 'position_chart' | 'position_roster';
  /** Stable idempotency key for a generated canonical projection diagram. */
  projectionKey?: string;
  /** Canonical folder that owns projected facts when this diagram is in a child folder. */
  sourceFolderId?: string;
  /** Opaque reference to the server-owned definition for a saved Atlas People view. */
  savedViewId?: string;
  /** Optional effective date for the projection. */
  asOf?: string | null;
  /** Diagram-owned projection/presentation filters. */
  filters?: Record<string, unknown>;
  /** For `type: 'network-design'` — parent folder to hydrate from. */
  baselineFolderId?: string;
  /** For `type: 'network-design'` — ISO timestamp captured at fork. */
  baselineImportedAt?: string;
}

export interface DiagramInfo {
  id: string;
  name: string;
  type: DiagramType;
  folderId: string;
  viewDefinition?: DiagramViewDefinition | null;
}

export function useDiagramInfo(diagramId?: string) {
  const fetcher = useAuthenticatedFetch();
  const enabled = Boolean(diagramId) && diagramId !== 'test';

  return useQuery<DiagramInfo>({
    queryKey: ['diagram', diagramId, 'info'],
    enabled,
    queryFn: async () => {
      let response = await fetcher(`/api/diagrams/${diagramId}`);
      // Fallback for link-token access (shared links): try the link endpoint.
      if (!response.ok) {
        response = await fetcher(`/api/link/diagrams/${diagramId}`);
      }
      if (!response.ok) {
        throw new Error(`Failed to fetch diagram info: ${response.statusText}`);
      }
      const data = await response.json();
      const diagram = (data.diagram || data?.diagram) ?? data?.data?.diagram;
      return {
        id: diagram.id,
        name: diagram.name,
        type: diagram.type as DiagramType,
        folderId: diagram.folderId,
        viewDefinition: diagram.viewDefinition ?? null,
      };
    },
    staleTime: 60_000,
  });
}
